/**
 * --------------------------------
 * FilterBox
 * ================================
 */

import * as React from 'react';
import isEqual from 'lodash/isEqual';
import { Button } from 'semantic-ui-react';
import { getNestedValue } from '../../utils/utils';

import { FilterDrawer } from './FilterDrawer';
import { ChipDrawer } from './ChipDrawer';

// used for the "none" filter in the query params
const NONE_GENERIC_VALUE = 'none';

export class FilterBox extends React.Component {
  constructor() {
    super();
    this.state = {
      isDrawerVisible: false,
      filterOptions: []
    };
  }

  validateCriticalProps = () => {
    const { ErrorHandler, data, filterCategories } = this.props;
    if (data && data.length > 0 && !!filterCategories) {
      const sampleRow = data[0];
      const sampleKeys = Object.keys(sampleRow);

      filterCategories.forEach((cat) => {
        if (!sampleKeys.includes(cat.name)) {
          ErrorHandler.bug(
            `ERROR::FilterBox::FilterCategory ${cat.title} dataKey "${cat.name}" is invalid!`
          );
        }
      });
    }
  };

  /* Get the page filters from the URL Query Params, form it similar to the previously used redux filters */
  getPageFilters = () => {
    const res = [];
    this.props.filterCategories.forEach((fc) => {
      const applicableFilter = this.props.queryParams[fc.name];
      if (applicableFilter) {
        let vals = applicableFilter.split('|');
        if (fc.noneAlias && vals.includes(NONE_GENERIC_VALUE)) {
          vals = vals.map((v) => (v === NONE_GENERIC_VALUE ? fc.noneAlias : v));
        }
        res.push({ name: fc.name, values: vals });
      }
    });
    return res;
  };

  filterData = (data) => {
    this.validateCriticalProps();

    let filterOptions = this.props.filterCategories;
    // reset / init options on each category.
    filterOptions.forEach((opt) => (opt.options = []));
    const selectedFilters = this.getPageFilters() || [];

    /**
     * In this first loop we will figure out
     * 1) if there are selected filter(s),
     * 2) and if so, does this row of data match each filter category
     */
    const filteredData = data.filter((row) => {
      let keepIt = true;
      let selectedFiltersExist = selectedFilters.length > 0;
      let didItMatchOneFromEverySelectedFilterCategory = true;

      this.props.filterCategories.forEach((filter) => {
        // find item in the row of data that matches this filter category aka name
        const val = getNestedValue(filter.name, row);
        // find if we have a selected filter that matches this filter category aka name
        let selectedFilter = selectedFilters.find(
          (sf) => sf.name === filter.name
        );

        // if there IS a selected filter, then it MUST match at least one value in that category
        if (selectedFilter) {
          if (val && Array.isArray(val)) {
            const foundAMatch = val.find(
              (v) =>
                selectedFilter.values && selectedFilter.values.indexOf(v) !== -1
            );
            // so if we didn't find a match in the whole array, then we wont add the filter
            if (!foundAMatch) {
              didItMatchOneFromEverySelectedFilterCategory = false;
            }
          } else if (
            // if there isn't a value for this field AND there is not a nullAlias
            (!filter.noneAlias && !val) ||
            // or, there is a nullAlias, but it is not selected, and the value is null
            (filter.noneAlias &&
              selectedFilter.values.indexOf(filter.noneAlias) === -1 &&
              !val) ||
            // or if there is a value, but the value isn't in the selected list.
            (val &&
              selectedFilter.values &&
              selectedFilter.values.indexOf(val) === -1)
          ) {
            // then it didn't match all the selected filters
            didItMatchOneFromEverySelectedFilterCategory = false;
          }
        }
      });

      /**
       * Now we should know if the row matched all the selected filter categories, if they exist,
       * so now we will filter our data, and build our filter options
       */
      this.props.filterCategories.forEach((filter) => {
        // find item in the row of data that matches this filter
        const val = getNestedValue(filter.name, row);

        // we want to build a filteroptions list of all options if no filter is selected
        // but if one IS selected, then we want to only build options that match all of the filters
        const shouldWeAddItToFilterOptions = selectedFiltersExist
          ? didItMatchOneFromEverySelectedFilterCategory
          : true;

        keepIt = shouldWeAddItToFilterOptions;

        if (
          shouldWeAddItToFilterOptions &&
          (filter.noneAlias ||
            (val !== null && val !== undefined && val !== ''))
        ) {
          filterOptions = filterOptions.map((f) => {
            if (f.name === filter.name) {
              // if you pass an array of options for a single field, add each individually
              let valArr = Array.isArray(val) ? val : [val];
              valArr.forEach((v) => {
                let addedIt = false;
                f.options = f.options || [];
                f.options.forEach((o) => {
                  if (o.value === v) {
                    addedIt = true;
                    o.count += 1;
                  } else if (f.noneAlias && o.value === f.noneAlias && !v) {
                    addedIt = true;
                    o.count += 1;
                  }
                });
                if (!addedIt) {
                  if (f.noneAlias && !v) {
                    f.options.push({ value: f.noneAlias, count: 1 });
                  } else {
                    f.options.push({ value: v, count: 1 });
                  }
                }
              });
            }
            return f;
          });
        }
      });
      return keepIt;
    });

    filterOptions = filterOptions.map((f) => {
      f.options = f.options
        ? f.options.sort((a, b) =>
            a.count > b.count ? -1 : b.count > a.count ? 1 : 0
          )
        : [];
      return f;
    });

    this.setState({ filterOptions }, () => {
      const totalCount = this.props.data?.length || 0;
      const selectedFilters = this.getPageFilters() || [];
      const isFiltered = selectedFilters.length > 0;

      this.props.onChange({
        filteredData,
        isFiltered,
        totalCount
      });
    });
  };

  handleClearFilters = (e) => {
    e.preventDefault();
    const qpToClear = {};
    this.props.filterCategories
      .map((cat) => cat.name)
      .forEach((n) => {
        qpToClear[n] = undefined;
      });
    this.props.setQueryParams(qpToClear);
  };

  handleDeselectChip = ({ name, page }) => {
    this.props.setQueryParams({ [name]: undefined });
    // force an update since 'selectedFilters' are not tracked on state.
    // maybe consider moving 'selectedFilters' to state at some point
    this.setState({ ...this.state });
  };

  handleFilterChange = ({ name, title, value, selected }) => {
    const thisFilter = this.props.filterCategories.find(
      (fc) => fc.name === name
    );
    const fixedValue =
      thisFilter?.noneAlias && thisFilter.noneAlias === value ? 'none' : value;
    const currentValueOfParam = this.props.queryParams[name];
    if (selected) {
      let newValue = fixedValue;
      if (currentValueOfParam) {
        // if there is already a query param with this category, ie, we're selecting more than one
        newValue = `${currentValueOfParam}|${newValue}`;
      }
      this.props.setQueryParams({ [name]: newValue });
    } else {
      let newValue = undefined;
      if (currentValueOfParam && currentValueOfParam.indexOf('|') !== -1) {
        // if there is an associated value, AND that value has a comma, ie, there are multiple selected values,
        // then lets only remove our value and leave the rest.
        newValue = currentValueOfParam
          .split('|')
          .filter((v) => v !== fixedValue)
          .join('|');
      }
      this.props.setQueryParams({ [name]: newValue });
    }
  };

  handleToggleDrawer = () => {
    // TODO: fix this!!
    this.setState({ isDrawerVisible: !this.state.isDrawerVisible });
  };

  componentDidMount() {
    // set qp from redux state if it exists
    const pf = this.getPageFilters();
    if (pf && pf.filters && pf.filters.length > 0) {
      const newQP = {};
      pf.filters.forEach((f) => (newQP[f.name] = f.values.join('|')));
      this.props.setQueryParams(newQP);
    }
    this.filterData(this.props.data);
  }

  componentDidUpdate(prevProps, prevState) {
    let isDifferent = false;
    // only update based on applicable query param changes
    this.props.filterCategories.forEach((fc) => {
      const prevVal = prevProps.queryParams[fc.name];
      const currVal = this.props.queryParams[fc.name];
      if (prevVal !== currVal) {
        isDifferent = true;
      }
    });
    if (isDifferent) {
      this.filterData(this.props.data);
    }
    if (!isEqual(prevProps.data, this.props.data)) {
      this.filterData(this.props.data);
    }
  }

  render() {
    const { page, filterCategories } = this.props;
    const { isDrawerVisible, filterOptions } = this.state;
    const selectedFilters = this.getPageFilters() || [];
    const defaultStyle = {
      transition: 'all 300ms cubic-bezier(0.08, 0.68, 0.46, 1.06)',
      maxHeight: 0,
      opacity: 0
    };
    const transitionStyles = {
      transition: 'all 500ms cubic-bezier(0.08, 0.68, 0.46, 1.06)',
      maxHeight: '374px',
      opacity: 1
    };
    return (
      <div className="FilterBox">
        {!!filterCategories?.length && (
          <React.Fragment>
            <div className="buttons-and-chips">
              <Button
                onClick={this.handleToggleDrawer}
                className="drawerToggle">
                <span>Filter</span>
                <i
                  className={['chevron down icon', isDrawerVisible && 'open']
                    .filter((f) => !!f)
                    .join(' ')}
                />
              </Button>

              <ChipDrawer
                selectedFilters={selectedFilters}
                page={page}
                handleDeselectChip={this.handleDeselectChip}
                filterCategories={this.props.filterCategories}
              />
            </div>
            <FilterDrawer
              onClose={this.handleToggleDrawer}
              style={{
                ...defaultStyle,
                ...(isDrawerVisible ? transitionStyles : {})
              }}
              filterOptions={filterOptions}
              selectedFilters={selectedFilters}
              handleFilterChange={this.handleFilterChange}
              handleClearFilters={this.handleClearFilters}
            />
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default FilterBox;
